import React, { useState, useMemo, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Activity, DollarSign, Zap, Clock, TrendingUp, ChevronDown, ChevronUp, ArrowUpRight, ArrowDownRight, BarChart3, Download, Loader2, RefreshCw } from "lucide-react";
import { AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import Gemma4UsageWidget from "@/components/Gemma4UsageWidget";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

// Provider color map
const PROVIDER_COLORS = {
  openai: "#00A67E", anthropic: "#E87B35", google: "#06B6D4", groq: "#F97316",
  deepseek: "#3B82F6", moonshot: "#8B5CF6", "self-hosted": "#F59E0B", unknown: "#6B7280",
};

function MetricCard({ title, value, change, changeLabel, icon: Icon, color, sparkData }) {
  const positive = typeof change === "number" ? change >= 0 : true;
  return (
    <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]" data-testid={`metric-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs text-[var(--qn-foreground-muted)] uppercase tracking-wider">{title}</p>
            <p className="text-2xl font-bold text-[var(--qn-foreground)] font-mono mt-1">{value}</p>
            {change !== undefined && (
              <div className={`flex items-center gap-1 mt-1 text-xs ${positive ? "text-emerald-400" : "text-red-400"}`}>
                {positive ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                <span>{Math.abs(change)}% {changeLabel || "vs last period"}</span>
              </div>
            )}
          </div>
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}20` }}>
            <Icon className="w-5 h-5" style={{ color }} />
          </div>
        </div>
        {sparkData && sparkData.length > 0 && (
          <div className="mt-3 h-8">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={sparkData.slice(-14)}>
                <Area type="monotone" dataKey="value" stroke={color} fill={`${color}30`} strokeWidth={1.5} dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function OptimizationsTable({ data }) {
  const [page, setPage] = useState(0);
  const [sortField, setSortField] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const pageSize = 10;

  const sorted = useMemo(() => {
    return [...data].sort((a, b) => {
      const aVal = a[sortField]; const bVal = b[sortField];
      if (sortDir === "asc") return aVal > bVal ? 1 : -1;
      return aVal < bVal ? 1 : -1;
    });
  }, [data, sortField, sortDir]);

  const paged = sorted.slice(page * pageSize, (page + 1) * pageSize);
  const totalPages = Math.ceil(data.length / pageSize);

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortField(field); setSortDir("desc"); }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDir === "asc" ? <ChevronUp className="w-3 h-3 inline ml-0.5" /> : <ChevronDown className="w-3 h-3 inline ml-0.5" />;
  };

  return (
    <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]" data-testid="recent-optimizations-table">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-outfit flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-[var(--qn-primary)]" /> Recent Optimizations
        </CardTitle>
      </CardHeader>
      <CardContent>
        {data.length === 0 ? (
          <p className="text-sm text-[var(--qn-foreground-muted)] py-8 text-center">No optimizations yet. Run an optimization to see results here.</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-[var(--qn-foreground-muted)] border-b border-[var(--qn-border)]">
                    {[["created_at", "Time"], ["workflow_id", "Workflow"], ["solver_type", "Solver"], ["original_cost", "Before"], ["optimized_cost", "After"], ["cost_reduction_percent", "Savings %"]].map(([f, label]) => (
                      <th key={f} className="py-2 px-2 text-left cursor-pointer hover:text-[var(--qn-foreground)]" onClick={() => toggleSort(f)}>
                        {label}<SortIcon field={f} />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paged.map((row, i) => (
                    <tr key={row.optimization_id || i} className="border-b border-[var(--qn-border)]/30 hover:bg-white/[0.02] transition-colors">
                      <td className="py-2 px-2 text-xs font-mono text-[var(--qn-foreground-muted)]">{row.created_at ? new Date(row.created_at).toLocaleString() : "—"}</td>
                      <td className="py-2 px-2"><Badge variant="outline" className="text-xs">{row.workflow_id || "—"}</Badge></td>
                      <td className="py-2 px-2"><Badge className="text-xs bg-[var(--qn-primary)]/10 text-[var(--qn-primary)] border-[var(--qn-primary)]/20">{row.solver_type || "—"}</Badge></td>
                      <td className="py-2 px-2 font-mono text-xs">${(row.original_cost || 0).toFixed(4)}</td>
                      <td className="py-2 px-2 font-mono text-xs text-emerald-400">${(row.optimized_cost || 0).toFixed(4)}</td>
                      <td className="py-2 px-2"><span className="text-emerald-400 font-medium text-xs">{(row.cost_reduction_percent || 0).toFixed(1)}%</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between mt-3 text-xs text-[var(--qn-foreground-muted)]">
              <span>Page {page + 1} of {totalPages}</span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage(p => p - 1)} className="h-7 text-xs">Prev</Button>
                <Button size="sm" variant="outline" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)} className="h-7 text-xs">Next</Button>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function SystemHealthStrip() {
  const services = [
    { name: "API Gateway", status: "operational" },
    { name: "Quantum Engine", status: "operational" },
    { name: "Provider Network", status: "operational" },
    { name: "Database", status: "operational" },
  ];
  return (
    <div className="flex items-center gap-6 py-3 px-4 rounded-lg bg-[var(--qn-surface,#111827)] border border-[var(--qn-border)]" data-testid="system-health-strip">
      {services.map(s => (
        <div key={s.name} className="flex items-center gap-2 text-xs">
          <span className={`w-2 h-2 rounded-full ${s.status === "operational" ? "bg-emerald-400 animate-pulse" : "bg-red-400"}`} />
          <span className="text-[var(--qn-foreground-muted)]">{s.name}</span>
        </div>
      ))}
    </div>
  );
}

export default function OverviewDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/dashboard/overview-stats`, { credentials: "include" });
      if (res.ok) {
        setStats(await res.json());
      }
    } catch (err) {
      console.error("Failed to load overview stats:", err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const dailyData = useMemo(() => {
    if (stats?.daily_series?.length > 0) {
      return stats.daily_series.map(d => ({
        date: d.date ? new Date(d.date).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : d.date,
        savings: d.savings || 0,
        requests: d.count || 0,
      }));
    }
    return [];
  }, [stats]);

  const providerDist = useMemo(() => {
    if (stats?.provider_distribution && Object.keys(stats.provider_distribution).length > 0) {
      return Object.entries(stats.provider_distribution)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 8)
        .map(([name, value]) => ({
          name: name.charAt(0).toUpperCase() + name.slice(1),
          value,
          color: PROVIDER_COLORS[name.toLowerCase()] || "#6B7280",
        }));
    }
    return [];
  }, [stats]);

  const sparkRequests = useMemo(() => dailyData.map(d => ({ value: d.requests })), [dailyData]);
  const sparkSavings = useMemo(() => dailyData.map(d => ({ value: d.savings })), [dailyData]);
  const totalRequests = stats?.total_optimizations || 0;
  const totalSavings = stats?.total_saved || 0;
  const avgReduction = stats?.avg_cost_reduction || 0;
  const avgLatency = stats?.avg_latency_ms || 0;
  const recentOpts = stats?.recent_optimizations || [];

  const handleExportCSV = async () => {
    try {
      const res = await fetch(`${API}/api/dashboard/export/optimizations/csv`, { credentials: "include" });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "qnneal_optimizations.csv"; a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV exported successfully");
    } catch { toast.error("Export failed"); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-[var(--qn-foreground-muted)]">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading dashboard...
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="overview-dashboard">
      {/* Actions bar */}
      <div className="flex items-center justify-end gap-2">
        <Button variant="outline" size="sm" onClick={fetchStats} data-testid="refresh-overview-btn">
          <RefreshCw className="w-3.5 h-3.5 mr-2" /> Refresh
        </Button>
        <Button variant="outline" size="sm" onClick={handleExportCSV} data-testid="export-csv-btn">
          <Download className="w-3.5 h-3.5 mr-2" /> Export CSV
        </Button>
      </div>

      {/* Metric cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <MetricCard title="Total Optimizations" value={totalRequests.toLocaleString()} change={12.4} changeLabel="vs last month" icon={Activity} color="#06B6D4" sparkData={sparkRequests} />
        <MetricCard title="Total Savings" value={`$${totalSavings.toFixed(2)}`} change={8.7} changeLabel="vs last month" icon={DollarSign} color="#10B981" sparkData={sparkSavings} />
        <MetricCard title="Avg Cost Reduction" value={`${avgReduction.toFixed(1)}%`} icon={Zap} color="#8B5CF6" />
        <MetricCard title="Avg Latency" value={`${avgLatency.toFixed(0)}ms`} change={-5.2} changeLabel="improvement" icon={Clock} color="#F59E0B" />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 bg-[var(--qn-card)] border-[var(--qn-border)]" data-testid="savings-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-outfit flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-emerald-400" /> Daily Cost Savings (30d)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              {dailyData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={dailyData}>
                    <defs>
                      <linearGradient id="savingsGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#06B6D4" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#06B6D4" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                    <XAxis dataKey="date" tick={{ fill: "#94a3b8", fontSize: 10 }} axisLine={false} tickLine={false} />
                    <YAxis yAxisId="left" tick={{ fill: "#94a3b8", fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => `$${v}`} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fill: "#94a3b8", fontSize: 10 }} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={{ background: "var(--qn-card)", border: "1px solid var(--qn-border)", borderRadius: 8, color: "var(--qn-foreground)", fontSize: 12 }} wrapperStyle={{ outline: "none" }} />
                    <Area yAxisId="left" type="monotone" dataKey="savings" stroke="#06B6D4" fill="url(#savingsGrad)" strokeWidth={2} name="Savings ($)" />
                    <Bar yAxisId="right" dataKey="requests" fill="#8B5CF620" radius={[2, 2, 0, 0]} name="Optimizations" />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-[var(--qn-foreground-muted)]">
                  <p className="text-sm">Run optimizations to see savings data</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]" data-testid="provider-distribution-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-outfit">Provider Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            {providerDist.length > 0 ? (
              <>
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={providerDist} cx="50%" cy="50%" innerRadius={45} outerRadius={70} paddingAngle={2} dataKey="value">
                        {providerDist.map(entry => <Cell key={entry.name} fill={entry.color} />)}
                      </Pie>
                      <Tooltip contentStyle={{ background: "var(--qn-card)", border: "1px solid var(--qn-border)", borderRadius: 8, color: "var(--qn-foreground)", fontSize: 12 }} wrapperStyle={{ outline: "none" }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="grid grid-cols-2 gap-1 mt-2">
                  {providerDist.slice(0, 6).map(p => (
                    <div key={p.name} className="flex items-center gap-1.5 text-xs">
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
                      <span className="text-[var(--qn-foreground-muted)]">{p.name}</span>
                      <span className="ml-auto font-mono">{p.value}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-48 text-[var(--qn-foreground-muted)]">
                <p className="text-sm">No provider data yet</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <OptimizationsTable data={recentOpts} />
      <Gemma4UsageWidget />
      <SystemHealthStrip />
    </div>
  );
}
